import { useCallback, useEffect, useRef } from 'react';
import { usePicoXTrack } from '@eventbrite/bsp-sdk/react';
import {
  buildPicoXEventPageOpenedPayload,
  buildPicoXRelatedEventsRecommendedPayload,
  PicoXAdsEventNames,
  type PicoXPayloadValue,
} from '@/tracking';
import { RelatedEvent } from '../types';

/**
 * Stable wire-level surface name reported to PicoX as `product_location` for
 * the related-events surface on the listing page. Must match the eb-ui
 * counterpart (`packages/attendee/event-recommendations/src/tracking.ts`,
 * `RELATED_EVENTS_PLACEMENT_NAME`) so PicoX dashboards see one shape across
 * both frontends.
 */
export const RELATED_EVENTS_PRODUCT_LOCATION = 'related_events';

export interface UseRelatedEventsPicoXTrackingArgs {
  eventsToShow: RelatedEvent[];
  /**
   * Public ID of the event whose page is rendering the related-events
   * surface (i.e. the source/page event). Becomes PicoX `eb_event_id` per
   * the PicoX Tracking spec's `event_id` property on the slate event. On a
   * series-child listing this is the child id — that matches what the
   * legacy `/api/v3/log_engagement/` endpoint sends as
   * `retrieval_context.related_event_id`, and lets PicoX dashboards keyed
   * on `eb_event_id` keep segmenting by source surface.
   */
  surfaceEventId: string;
  /**
   * The series/timed-entry parent of `surfaceEventId` when applicable, else
   * equal to it. Becomes PicoX `parent_eb_event_id`. Derived at the call
   * site (`RelatedEvents.tsx`) from `BasicInfo.isSeries && seriesId`, so
   * series-child surfaces roll up to the series-parent id and standalone
   * surfaces collapse back to `surfaceEventId` (preserving the
   * pre-existing wire shape). `log_engagement` continues to receive the
   * child id via `createTrackingInformationGetter`, so the legacy
   * `retrieval_context.related_event_id` does not move.
   */
  parentEventId: string;
  /**
   * Tracks the combined loading state of `useRelatedAndPromotedEventsLazy`'s
   * organic + promoted fetches. Required so we wait to dispatch until BOTH
   * fetches settle — without it, `eventsToShow` updates twice during loading
   * (once when organic resolves, again when promoted merges in) and the
   * hook could dispatch with an incomplete slate. Belt: the
   * `lastDispatchedSurfaceRef` guard below also caps the dispatch to one
   * per surfaceEventId, so any post-settle `eventsToShow` change is a
   * no-op.
   */
  isLoading: boolean;
}

export interface UseRelatedEventsPicoXTrackingResult {
  trackEventPageOpened: (event: RelatedEvent) => void;
}

/**
 * Encapsulates the related-events PicoX dispatch wiring kept in lockstep with
 * the eb-ui counterpart (PR eventbrite/eb-ui#36290):
 *
 *   - `related_eb_events_recommended` fires once per slate the attendee sees,
 *     before any click activity is logged against it.
 *   - `eb_event_page_opened` fires when an attendee clicks a related-events
 *     card; the card invokes the returned callback synchronously *before*
 *     `window.open` so the event isn't dropped by the about-to-unload page.
 *
 * Both are additive to the existing `log_engagement` / Datadog
 * tracking. Lives in a dedicated hook so the `RelatedEvents` component body
 * stays under the complexity / many-returns thresholds.
 */
export const useRelatedEventsPicoXTracking = ({
  eventsToShow,
  surfaceEventId,
  parentEventId,
  isLoading,
}: UseRelatedEventsPicoXTrackingArgs): UseRelatedEventsPicoXTrackingResult => {
  const trackPicoXEvent = usePicoXTrack();

  // One slate impression per surfaceEventId per hook lifetime. The
  // `isLoading` gate alone is insufficient: any post-settle change to
  // `eventsToShow` (e.g. a downstream merge/cap re-derivation when an
  // Orion flag resolves after first paint, a slow promoted-events RPC
  // trickling in, a parent re-render swapping the slice) would otherwise
  // re-trigger this effect and bill PicoX a second impression for the
  // same user-perceived slate. Keyed on surfaceEventId (the source/page
  // event, not the series-parent fold) so a soft SPA navigation between
  // distinct child pages of the same series still dispatches a fresh
  // impression for the new child surface — each child page has its own
  // user-perceived slate even though they share a series parent.
  const lastDispatchedSurfaceRef = useRef<string | null>(null);

  useEffect(() => {
    if (isLoading || eventsToShow.length === 0) {
      return;
    }
    if (lastDispatchedSurfaceRef.current === surfaceEventId) {
      return;
    }
    lastDispatchedSurfaceRef.current = surfaceEventId;

    const payload = buildPicoXRelatedEventsRecommendedPayload({
      events: eventsToShow,
      productLocation: RELATED_EVENTS_PRODUCT_LOCATION,
      // `eventId` -> PicoX `eb_event_id` (the source/page event).
      // `parentEventId` -> PicoX `parent_eb_event_id` (the series parent
      // when on a series-child page, else the source event itself).
      eventId: surfaceEventId,
      parentEventId,
    });

    trackPicoXEvent(PicoXAdsEventNames.RelatedEventsRecommended, {
      ...(payload as Record<string, PicoXPayloadValue>),
    });
  }, [eventsToShow, surfaceEventId, parentEventId, trackPicoXEvent, isLoading]);

  const trackEventPageOpened = useCallback(
    (event: RelatedEvent) => {
      const payload = buildPicoXEventPageOpenedPayload({
        event,
        productLocation: RELATED_EVENTS_PRODUCT_LOCATION,
        eventSource: RELATED_EVENTS_PRODUCT_LOCATION,
        // The clicked card's `eb_event_page_opened` event records the
        // *destination* event as `eb_event_id` (handled inside the builder)
        // and the *source surface's* series-parent as `parent_eb_event_id`
        // so click attribution lines up with the slate impression above.
        parentEventId,
      });

      trackPicoXEvent(PicoXAdsEventNames.EventPageOpened, {
        ...(payload as Record<string, PicoXPayloadValue>),
      });
    },
    [parentEventId, trackPicoXEvent],
  );

  return { trackEventPageOpened };
};
